import { Injectable, signal } from '@angular/core';
import PocketBase from 'pocketbase';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class PocketbaseService {
  public pb: PocketBase;
  public loggedIn = signal(false);

  constructor(
    private readonly router: Router
  ) {
    this.pb = new PocketBase(environment.baseUrl);
    this.pb.autoCancellation(false)
    
    if (this.pb.authStore.isValid) {
      this.loggedIn.set(true);
    } else {
      this.loggedIn.set(false);
    }

    if (this.isMCN()) {
      this.logout();
    }
  }

  /**
   * due to an issue in Safari, a window has to be provided that's been opened with window.open()
   * from a **non-async** method! you can do this by calling openBrowserPopup() before calling this method
   * https://github.com/pocketbase/pocketbase/discussions/2429#discussioncomment-7334898
   */
  async socialLogin(provider: AuthProvider, w?: Window | null) {
    if (w) {
      await this.pb.collection('users').authWithOAuth2({
        provider,
        urlCallback: url => {
          w.location.href = url;
          w.focus();
        }
      });
    } else {
      await this.pb.collection('users').authWithOAuth2({ provider });
    }

    this.loggedIn.set(this.pb.authStore.isValid);
    if (!this.loggedIn) {
      throw new Error('Login failed');
    }
  }

  async login(email: string, password: string) {
    await this.pb.collection('users').authWithPassword(email, password);

    this.loggedIn.set(this.pb.authStore.isValid);
    
    if (!this.loggedIn) {
      throw new Error('Login failed');
    }
  }

  isMCN(): boolean {
    if (this.pb.authStore.model && this.pb.authStore.model['email']) {
      const email: string = this.pb.authStore.model['email'];
      return email.endsWith('my-career-now.de')
    }

    return false;
  }

  /**
   * helper method stolen from https://github.com/pocketbase/js-sdk/blob/04f9ae50dd09a0d5d148f97e7bf0208e1cc4b206/src/services/RecordService.ts
   */
  openBrowserPopup(): Window | null {
    let width = 1024;
    let height = 768;

    let windowWidth = window.innerWidth;
    let windowHeight = window.innerHeight;

    // normalize window size
    width = width > windowWidth ? windowWidth : width;
    height = height > windowHeight ? windowHeight : height;

    let left = windowWidth / 2 - width / 2;
    let top = windowHeight / 2 - height / 2;

    // note: we don't use the noopener and noreferrer attributes since
    // for some reason browser blocks such windows then url is undefined/blank
    return window.open(
      undefined,
      "popup_window",
      "width=" +
      width +
      ",height=" +
      height +
      ",top=" +
      top +
      ",left=" +
      left +
      ",resizable,menubar=no",
    );
  }

  logout() {
    this.pb.authStore.clear();
    this.loggedIn.set(false);
    this.router.navigate(['/login']);
  }

  async getCourseFileUrl(courseId: string, fileName: string, forceDownload = false): Promise<string> {
    const params = new URLSearchParams();

    if (forceDownload) {
      params.set('download', '1');
    }

    try {
      const token = await this.pb.files.getToken();
      if (token) {
        params.set('token', token);
      }
    } catch {
      // If token retrieval fails, return the URL without token and let server rules decide.
    }

    const query = params.toString();
    const baseUrl = `${this.pb.baseUrl}/api/files/courses/${courseId}/${encodeURIComponent(fileName)}`;
    return query ? `${baseUrl}?${query}` : baseUrl;
  }

}

export enum AuthProvider {
  Google = 'google', Microsoft = 'microsoft', Discord = 'discord'
}